import { ChartData } from 'components/chart/data/ChartData';
import { ChartDataArray } from 'components/chart/data/ChartDataArray';
import { ChartDataComparisonType } from 'components/chart/data/ChartDataComparisonType';
import { ChartDataType } from 'components/chart/data/ChartDataType';
import { LineChartConfig } from 'components/chart/data/LineChartConfig';
import { getChartColor } from 'components/chart/data/ChartColors';
import { DataEntry, DataResult } from 'models/data';
import { IntlShape } from 'react-intl';
import { DataRequest } from 'hooks/useDataRequest';
import { GroupByAttribute, TimeFilter } from 'services/data.service';
import { getLabelForDatapoint } from 'pages/insights-pro/graphs/graphUtils';
import { Attribute, AttributeCode, Measure } from 'services/model/model.service';
import { tbColors, tbPalette } from 'styles/colors';

export interface DateRangePickerLabelContext {
  dateRangeLabel?: string | null;
  comparisonRangeLabel?: string | null;
  translatedSafeDateRangeLabel: (intl: IntlShape) => string;
  translatedSafeComparisonRangeLabel: (intl: IntlShape) => string;
  translatedMeasureCardComparisonLabel: (intl: IntlShape) => string;
}

export interface CreateTimeSeriesChartConfigParams {
  getAttribute: (code: AttributeCode) => Attribute;
  measures: Measure[];
  request: DataRequest;
  intl: IntlShape;
  response: DataResult;
  labelContext?: DateRangePickerLabelContext;
  customDateRangeLabel?: string;
  customComparisonRangeLabel?: string;
  groupByAttribute?: GroupByAttribute;
  showDetectionDateMarker?: boolean;
}

export const buildTimeAttributeDataArray = (entries: DataEntry[], intl: IntlShape): ChartDataArray =>
  new ChartDataArray(
    ChartDataType.primary,
    '',
    entries.map(entry => new ChartData(getLabelForDatapoint(entry, intl), 0)) ?? []
  );

export const getLabelForDateRange = (dateRange: TimeFilter | null, intl: IntlShape) =>
  dateRange?.fromDate && dateRange?.toDate
    ? intl.formatDateTimeRange(new Date(dateRange.fromDate), new Date(dateRange.toDate))
    : '';

const createSingleMetricNoGroupingChartConfig = (
  measure: Measure,
  seriesData: DataEntry[],
  renderComparison: boolean,
  mainLineLabel: string,
  comparisonLineLabel: string,
  intl: IntlShape
): LineChartConfig => {
  const chartConfig = LineChartConfig.forTBMeasure(measure, false);

  chartConfig.data = [
    new ChartDataArray(
      ChartDataType.primary,
      mainLineLabel,
      seriesData.map(entry => new ChartData(getLabelForDatapoint(entry, intl), measure.getValue(entry.values)))
    ),
  ];
  if (renderComparison) {
    chartConfig.data.push(
      new ChartDataArray(
        ChartDataType.primary,
        comparisonLineLabel,
        seriesData.map(
          entry => new ChartData(getLabelForDatapoint(entry, intl), measure.getValue(entry.compareValues))
        ),
        ChartDataComparisonType.comparison
      )
    );
  }
  return chartConfig;
};

const createMultiMetricNoGroupingChartConfig = (
  measures: Measure[],
  seriesData: DataEntry[],
  intl: IntlShape
): LineChartConfig => {
  const chartConfig = LineChartConfig.forTBMeasure(measures[0], true);

  if (!seriesData) {
    return chartConfig;
  }

  chartConfig.data = [
    buildTimeAttributeDataArray(seriesData, intl),
    ...measures.map((measure, measureIdx) => {
      const array = new ChartDataArray(
        ChartDataType.breakdown,
        measure.metric.name,
        seriesData.map(entry => new ChartData(getLabelForDatapoint(entry, intl), measure.getValue(entry.values))),
        undefined
      );
      array.color = getChartColor(measureIdx);
      return array;
    }),
  ];
  return chartConfig;
};

const createSingleMetricWithGroupingChartConfig = (
  attribute: Attribute,
  measure: Measure,
  seriesData: DataEntry[],
  breakdownData: DataEntry[],
  intl: IntlShape
): LineChartConfig => {
  const chartConfig = LineChartConfig.forTBMeasure(measure, true);

  chartConfig.data = [
    buildTimeAttributeDataArray(seriesData, intl),
    ...breakdownData.map((line, lineIdx) => {
      const array = new ChartDataArray(
        ChartDataType.breakdown,
        line.isOther
          ? intl.formatMessage({
              id: 'groupby.others',
              defaultMessage: 'Others',
            })
          : attribute.getValue(line.attributes),
        line.sets.timeSeries.map(
          entry =>
            new ChartData(getLabelForDatapoint(entry, intl), measure.getValue(entry.values), undefined, undefined)
        ),
        undefined,
        line.isOther
      );
      array.color = getChartColor(lineIdx, false);
      return array;
    }),
  ];

  return chartConfig;
};

export const createTimeSeriesChartConfig = ({
  getAttribute,
  measures,
  request,
  response,
  intl,
  labelContext,
  customDateRangeLabel,
  customComparisonRangeLabel,
  groupByAttribute,
  showDetectionDateMarker,
}: CreateTimeSeriesChartConfigParams): LineChartConfig => {
  const isComparisonActive = Boolean(request.comparisonTimeFilter?.fromDate && request.comparisonTimeFilter?.toDate);

  /**
   * We have three combinations of charts:
   * (1) Single Metric, No Grouping - comparison may be active
   * (2) Single Metric, With Grouping - comparison is _not_ supported/enabled
   * (3) Multi Metric, No Grouping - comparison is _not_ supported/enabled
   */
  if (measures.length === 1) {
    if (!groupByAttribute) {
      // Variant (1) Single Metric, No Grouping
      const chartConfig = createSingleMetricNoGroupingChartConfig(
        measures[0],
        response.sets.timeSeries,
        isComparisonActive,
        customDateRangeLabel ??
          (labelContext?.dateRangeLabel
            ? intl.formatMessage({ id: labelContext.dateRangeLabel })
            : getLabelForDateRange(request.timeFilter, intl)),
        customComparisonRangeLabel ??
          (labelContext?.dateRangeLabel && labelContext?.comparisonRangeLabel
            ? intl.formatMessage({ id: labelContext.comparisonRangeLabel })
            : getLabelForDateRange(request.comparisonTimeFilter, intl)),
        intl
      );

      if (showDetectionDateMarker) {
        const lineColor = tbPalette.ui.inverseSecondary;

        chartConfig.data?.forEach(series => {
          if (series.type === ChartDataType.primary) {
            series.color = lineColor;
          }
        });

        chartConfig.highlightLineColor = tbPalette.surface.grey;
        chartConfig.detectionDateMarker = {
          label: intl.formatMessage({ id: 'chart.detectionDate', defaultMessage: 'Detection Date' }),
          color: tbColors.all.sharp[500],
          focusRingColor: tbColors.all.sharp[500],
        };
      }

      return chartConfig;
    }

    const attribute = getAttribute(groupByAttribute);
    // Variant (2) Single Metric, With Grouping
    return createSingleMetricWithGroupingChartConfig(
      attribute,
      measures[0],
      response.sets.timeSeries,
      response.sets.breakdown,
      intl
    );
  }
  // Variant (3) Multi Metric, No Grouping
  return createMultiMetricNoGroupingChartConfig(measures, response.sets.timeSeries, intl);
};
